/**
 * Company locale for money, dates and times.
 *
 * Amounts stay integer minor units (hundredths of the major unit) plus an ISO
 * 4217 currency code everywhere they are stored. This module only decides how
 * `Intl` renders them. Separators, symbol placement and grouping come from ICU
 * — never from hand-rolled punctuation.
 *
 * A missing locale means "keep the format this company already had": the
 * language's locale on the server, the browser's locale in the web app. That
 * is how every existing US company still reads. Saving `en-US` (or `en-ZA`)
 * is an explicit choice and wins over the browser.
 */

export const DEFAULT_LOCALE = 'en-US';

export const DATE_FORMATS = ['short', 'medium', 'long'] as const;
export type DateFormatStyle = (typeof DATE_FORMATS)[number];

export const TIME_FORMATS = ['12', '24'] as const;
export type TimeFormatStyle = (typeof TIME_FORMATS)[number];

/**
 * Locales the settings picker offers. Any other tag `Intl` accepts still
 * saves — the list is a shortcut, not the set of locales the app can format.
 */
export const COMMON_LOCALES = [
  'en-US',
  'en-ZA',
  'en-GB',
  'en-AU',
  'en-CA',
  'en-NG',
  'en-KE',
  'en-GH',
  'en-IN',
  'es-MX',
  'es-ES',
  'fr-FR',
  'pt-BR',
  'pt-PT',
] as const;

/** BCP 47, short enough for `companies.locale` (VARCHAR 35). */
const LOCALE_TAG = /^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8}){0,3}$/;

/**
 * A tag `Intl` can format with, in canonical form (`en-za` → `en-ZA`).
 * Null when the tag is empty, too long, or the runtime rejects it.
 */
export function canonicalLocale(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!LOCALE_TAG.test(trimmed) || trimmed.length > 35) return null;
  try {
    const [canonical] = Intl.getCanonicalLocales(trimmed);
    if (!canonical || canonical.length > 35) return null;
    // Throws on a tag this ICU build will not format. A tag it accepts and
    // then falls back from is still a locale — `en-ZA` must not be refused
    // because some other region is unknown.
    new Intl.NumberFormat(canonical, { style: 'currency', currency: 'USD' }).format(0);
    new Intl.DateTimeFormat(canonical, { dateStyle: 'medium' }).format(0);
    return canonical;
  } catch {
    return null;
  }
}

export function parseDateFormat(value: string | null | undefined): DateFormatStyle | null {
  return value === 'short' || value === 'medium' || value === 'long' ? value : null;
}

export function parseTimeFormat(value: string | null | undefined): TimeFormatStyle | null {
  return value === '12' || value === '24' ? value : null;
}

/**
 * The locale to hand `Intl` when a company has chosen one.
 * Null means the caller keeps its existing locale.
 */
export function explicitFormatLocale(value: string | null | undefined): string | null {
  return canonicalLocale(value);
}

/** Company locale when one is stored, otherwise `fallback` (language or browser). */
export function resolveDisplayLocale(companyLocale: string | null | undefined, fallback: string): string {
  return explicitFormatLocale(companyLocale) ?? fallback;
}

/** "en-ZA — English (South Africa)" where the runtime knows the name. */
export function localeLabel(tag: string): string {
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'language' }).of(tag);
    return name && name.toLowerCase() !== tag.toLowerCase() ? `${tag} — ${name}` : tag;
  } catch {
    return tag;
  }
}

export interface CompanyFormatPrefs {
  locale: string | null;
  dateFormat: DateFormatStyle | null;
  timeFormat: TimeFormatStyle | null;
}

const DATE_COMPONENT_FIELDS = ['weekday', 'era', 'year', 'month', 'day', 'dayPeriod', 'dateStyle'] as const;
const TIME_COMPONENT_FIELDS = [
  'hour',
  'minute',
  'second',
  'fractionalSecondDigits',
  'dayPeriod',
  'timeStyle',
  'hour12',
  'hourCycle',
] as const;

/**
 * Applies a company's date-style and 12/24-hour overrides to options that
 * already describe a date or a time.
 *
 * `dateStyle` cannot sit next to `year` / `month` / `day`, and neither can it
 * sit next to `hour` / `minute`. When a date override replaces those fields
 * and the original options also asked for a time, the time half becomes
 * `timeStyle: 'short'` so the pair stays legal. No override leaves the
 * options alone — that is what keeps an existing company's dates looking as
 * they do today.
 */
export function applyDateTimeFormatPrefs(
  options: Intl.DateTimeFormatOptions,
  prefs: { dateFormat?: DateFormatStyle | null; timeFormat?: TimeFormatStyle | null } | null | undefined,
): Intl.DateTimeFormatOptions {
  if (!prefs?.dateFormat && !prefs?.timeFormat) return options;
  const wantsDate = Boolean(options.dateStyle || options.weekday || options.year || options.month || options.day);
  const wantsTime = Boolean(options.timeStyle || options.hour || options.minute || options.second);
  if (!wantsDate && !wantsTime) return options;

  const next: Intl.DateTimeFormatOptions = { ...options };
  if (prefs.dateFormat && wantsDate) {
    for (const key of DATE_COMPONENT_FIELDS) delete next[key];
    next.dateStyle = prefs.dateFormat;
  }
  // A date style and individual time fields are not a legal combination.
  if (next.dateStyle && wantsTime && (next.hour || next.minute || next.second || next.hour12 || next.hourCycle)) {
    for (const key of TIME_COMPONENT_FIELDS) delete next[key];
    next.timeStyle = options.timeStyle ?? 'short';
  }
  if (prefs.timeFormat && wantsTime) {
    delete next.hour12;
    next.hourCycle = prefs.timeFormat === '24' ? 'h23' : 'h12';
  }
  return next;
}
